/**
 * Hook: useExpenseCapture
 *
 * Wires the Ingestion pipeline to the Capture UI.
 * Uses the real API when configured, falls back to in-process agents.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAppStore } from '@/stores/useAppStore';
import { api, isInLocalMode } from '@/lib/api';
import { useExpenseStore } from '@/stores/useExpenseStore';
import { transcribeVoiceAudio } from '@/lib/voice-capture';
import { startStreamTranscribe, canStreamVoice } from '@/lib/voice-stream';
import type { Expense } from '@/types';

/** A live streaming-voice session. The UI calls stop() on the stop tap. */
export interface VoiceStreamSession {
  /** Stop the mic, get the parsed expense (same shape as the batch path). */
  stop: () => Promise<CaptureResult>;
  /** Abort without using the result (e.g. user navigated away). */
  cancel: () => void;
}

/** Thrown by startVoiceStream when the monthly voice-minute cap is reached. */
export class VoiceBlockedError extends Error {
  readonly voiceBlocked = true;
}

interface CaptureResult {
  success: boolean;
  expense?: Partial<Expense>;
  compliancePassed?: boolean;
  confidence?: number;
  modelUsed?: string;
  error?: string;
  requiresReview?: boolean;
  // Voice: the transcript the audio/typed input was parsed from (so the UI can
  // echo "Heard: ..." when pre-filling the expense form).
  transcript?: string;
}

export function useExpenseCapture() {
  const { activeEntity, user } = useAppStore();
  const { addExpense } = useExpenseStore();
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<CaptureResult | null>(null);
  // Safety net: if the component using a live voice-stream session unmounts
  // (route change, sign-out, error boundary) without calling stop()/cancel(),
  // release the mic + tear down on unmount so it never stays hot.
  const activeSessionRef = useRef<VoiceStreamSession | null>(null);
  useEffect(() => () => { activeSessionRef.current?.cancel(); }, []);

  /** Submit a manual expense */
  const submitManual = useCallback(async (data: {
    vendor: string;
    amount: number;
    expenseDate: string;
    category: string;
    businessPurpose?: string;
    geoLat?: number;
    geoLng?: number;
    geoLocationName?: string;
    // IRC §264(a)(1) flag — only meaningful when category is
    // 'officer_life_insurance' (canonical) or matches the legacy substring
    // patterns. null = unclassified (conservative add-back on M-1).
    isEntityBeneficiary?: boolean | null;
    // IRC §274(b) business gift — only meaningful when the gift category is chosen.
    // ircCode is stamped '274(b)' for gifts so the $25/recipient/year cap engines recognize it.
    ircCode?: string;
    giftRecipient?: string;
    giftIncidentalAmount?: number;
  }): Promise<CaptureResult> => {
    if (!activeEntity || !user) {
      const result: CaptureResult = { success: false, error: 'No active entity' };
      setLastResult(result);
      return result;
    }

    setLoading(true);
    try {
      if (!isInLocalMode()) {
        // Use real API
        const response = await api.ingestManual({
          ...data,
          sourceVector: 'manual',
        }) as { expense: Partial<Expense>; compliancePassed?: boolean };

        const result: CaptureResult = {
          success: true,
          expense: response.expense,
          compliancePassed: response.compliancePassed,
        };
        if (result.expense) addExpense(result.expense);
        setLastResult(result);
        return result;
      }

      // Fallback: local-only mode — create expense locally
      const localExpense: Partial<Expense> = {
        id: crypto.randomUUID(),
        entityId: activeEntity.id,
        userId: user.id,
        vendor: data.vendor,
        amount: data.amount,
        expenseDate: data.expenseDate,
        category: data.category,
        sourceVector: 'manual',
        geoLat: data.geoLat,
        geoLng: data.geoLng,
        geoLocationName: data.geoLocationName,
      };
      addExpense(localExpense);
      const result: CaptureResult = { success: true, expense: localExpense, compliancePassed: false };
      setLastResult(result);
      return result;
    } catch (err) {
      const result: CaptureResult = {
        success: false,
        error: err instanceof Error ? err.message : 'Capture failed',
      };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  }, [activeEntity, user, addExpense]);

  /** Submit a camera/receipt expense */
  const submitCamera = useCallback(async (ocrResult: {
    vendor: string;
    amount: number;
    date: string;
    confidence: number;
    imageBase64?: string;
    // 2026-06-13 upload-403 fix: prefer s3Key (browser uploaded directly to S3
    // via presigned PUT, bypassing CloudFront/WAF's 8KB body limit). imageBase64
    // kept only as a fallback for tiny payloads / local mode.
    s3Key?: string;
  }): Promise<CaptureResult> => {
    if (!activeEntity || !user) {
      const result: CaptureResult = { success: false, error: 'No active entity' };
      setLastResult(result);
      return result;
    }

    setLoading(true);
    try {
      if (!isInLocalMode()) {
        const response = await api.ingestCamera(
          ocrResult.s3Key ? { s3Key: ocrResult.s3Key } : { imageBase64: ocrResult.imageBase64 },
        ) as { expense: Partial<Expense>; compliancePassed?: boolean; requiresReview?: boolean };

        const result: CaptureResult = {
          success: true,
          expense: response.expense,
          compliancePassed: response.compliancePassed,
          requiresReview: response.requiresReview,
          confidence: ocrResult.confidence,
        };
        setLastResult(result);
        return result;
      }

      // Fallback local
      const result: CaptureResult = { success: true, expense: { vendor: ocrResult.vendor, amount: ocrResult.amount }, confidence: ocrResult.confidence };
      setLastResult(result);
      return result;
    } catch (err) {
      const result: CaptureResult = { success: false, error: err instanceof Error ? err.message : 'Camera capture failed' };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  }, [activeEntity, user]);

  /**
   * Parse a typed ("type what you'd say") voice expression. Returns the parsed
   * expense for the caller to pre-fill the manual form — does NOT set lastResult
   * (the form-fill + manual Submit is what actually persists, via /expenses).
   */
  const submitVoice = useCallback(async (transcript: string): Promise<CaptureResult> => {
    if (!activeEntity || !user) {
      return { success: false, error: 'No active entity' };
    }
    if (isInLocalMode()) {
      return { success: true, expense: { vendor: 'Voice Entry' }, transcript };
    }
    try {
      const response = await api.ingestVoice({ transcript }) as { expense?: Partial<Expense>; transcript?: string };
      return { success: true, expense: response.expense, transcript: response.transcript ?? transcript };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Voice capture failed' };
    }
  }, [activeEntity, user]);

  /**
   * Given audio already uploaded to S3 (presigned PUT — the WAF-safe path),
   * start AWS Transcribe and poll until the parsed expense comes back. Audio
   * stays in our AWS account (no third-party STT). Returns the parsed expense
   * + transcript for the caller to create the expense or pre-fill the form.
   */
  const submitVoiceAudio = useCallback(async (audioS3Key: string, format: string): Promise<CaptureResult> => {
    if (!activeEntity || !user) {
      return { success: false, error: 'No active entity' };
    }
    if (isInLocalMode()) {
      return { success: false, error: 'Voice capture needs a live connection. Type it instead.' };
    }
    const result = await transcribeVoiceAudio((data) => api.ingestVoice(data), { audioS3Key, format });
    return {
      success: result.success,
      expense: result.expense as Partial<Expense> | undefined,
      transcript: result.transcript,
      error: result.error,
    };
  }, [activeEntity, user]);

  /**
   * One call for the voice UIs: upload a recorded audio Blob straight to S3 via
   * presigned PUT (WAF-safe), then transcribe + parse it (AWS Transcribe — audio
   * stays in our AWS account). Returns the parsed expense; the caller decides
   * whether to create it or pre-fill a form. Shared by Capture's Voice tab and
   * the floating VoiceFAB so the two can't drift.
   */
  const uploadAndTranscribeVoice = useCallback(async (blob: Blob, format: string): Promise<CaptureResult> => {
    if (!activeEntity || !user) return { success: false, error: 'No active entity' };
    if (isInLocalMode()) return { success: false, error: 'Voice capture needs a live connection.' };
    try {
      const contentType = `audio/${format}`;
      const { uploadUrl, s3Key } = await api.getUploadUrl({ contentType });
      const put = await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
      if (!put.ok) return { success: false, error: `Upload failed (HTTP ${put.status}). Please try again.` };
      const result = await transcribeVoiceAudio((data) => api.ingestVoice(data), { audioS3Key: s3Key, format });
      return {
        success: result.success,
        expense: result.expense as Partial<Expense> | undefined,
        transcript: result.transcript,
        error: result.error,
      };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Voice capture failed' };
    }
  }, [activeEntity, user]);

  /**
   * Faster voice: open a LIVE AWS Transcribe streaming session (browser-direct,
   * ~1-2s with partials as you speak). The returned session's stop() returns the
   * parsed expense in the SAME shape as uploadAndTranscribeVoice, so the caller's
   * existing "create or pre-fill" logic is unchanged. Throws if streaming is
   * unavailable (caller falls back to the batch path) or VoiceBlockedError if the
   * monthly minute cap is reached (caller shows the reason, does NOT fall back).
   */
  const startVoiceStream = useCallback(async (
    onPartial?: (text: string) => void,
    onAutoStop?: () => void,
  ): Promise<VoiceStreamSession> => {
    if (!activeEntity || !user) throw new Error('No active entity');
    if (isInLocalMode()) throw new Error('local mode');
    if (!canStreamVoice()) throw new Error('streaming unavailable');

    // Soft advisory pre-flight: block only on an explicit "cap reached"; any
    // network/accounting error fails OPEN so voice is never blocked by a glitch.
    try {
      const pf = await api.voicePreflight();
      if (pf && pf.allowed === false) {
        throw new VoiceBlockedError(pf.reason || 'Monthly voice limit reached. Type it instead.');
      }
    } catch (e) {
      if (e instanceof VoiceBlockedError) throw e;
      // ignore preflight errors -> proceed
    }

    const controller = await startStreamTranscribe({ onPartial, onAutoStop });
    const session: VoiceStreamSession = {
      stop: async (): Promise<CaptureResult> => {
        activeSessionRef.current = null;
        const { transcript, seconds } = await controller.stop();
        if (seconds > 0) void api.logVoiceUsage(seconds).catch(() => {});
        if (!transcript) return { success: false, error: "Didn't catch that — try again." };
        // Hand the final transcript to the existing parse->expense path.
        return await submitVoice(transcript);
      },
      cancel: () => {
        activeSessionRef.current = null;
        controller.abort(); // immediate mic release (cancel/unmount)
      },
    };
    activeSessionRef.current = session;
    return session;
  }, [activeEntity, user, submitVoice]);

  return {
    loading,
    lastResult,
    submitManual,
    submitCamera,
    submitVoice,
    submitVoiceAudio,
    uploadAndTranscribeVoice,
    startVoiceStream,
    voiceStreamingAvailable: canStreamVoice(),
    clearResult: () => setLastResult(null),
  };
}
